/** Decode MIME encoded-words and quoted-printable artifacts for display. */

const ENCODED_WORD_RE = /=\?([^?]+)\?([BbQq])\?([^?]*)\?=/g;
const QP_BYTE_RE = /=([0-9A-Fa-f]{2})/g;

export function decodeMimeText(raw: string | null | undefined): string {
  if (raw == null || raw === '') return '';
  const input = typeof raw === 'string' ? raw : String(raw);
  let text = input.replace(/\?=\s+=\?/g, '?==?');
  text = text.replace(ENCODED_WORD_RE, (_, _charset, encoding, payload) => {
    try {
      if (encoding.toUpperCase() === 'B') {
        return atob(payload.replace(/\s/g, ''));
      }
      const bytes: number[] = [];
      const normalized = payload.replace(/_/g, ' ').replace(/=([0-9A-Fa-f]{2})/g, (_m, hex) => {
        bytes.push(parseInt(hex, 16));
        return '';
      });
      for (let i = 0; i < normalized.length; i += 1) {
        bytes.push(normalized.charCodeAt(i));
      }
      return new TextDecoder('utf-8').decode(new Uint8Array(bytes));
    } catch {
      return payload;
    }
  });
  text = text.replace(/=\r?\n/g, '');
  text = text.replace(QP_BYTE_RE, (_m, hex) => String.fromCharCode(parseInt(hex, 16)));
  return text.trim();
}

/** Neutral stand-in for an incident with no usable title — it must not read
 * as a classification (operational vs engineering). */
export const UNTITLED_INCIDENT = 'Untitled incident';

export function displayIncidentTitle(title: string | null | undefined | unknown): string {
  if (title == null || title === '') return UNTITLED_INCIDENT;
  if (typeof title === 'object') return UNTITLED_INCIDENT;
  return decodeMimeText(String(title)) || UNTITLED_INCIDENT;
}

function stripHtmlTags(html: string): string {
  return html
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|tr|li|h[1-6])>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/<[^>]*$/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/\s+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
}

function extractHtmlTitle(html: string): string {
  const match = html.match(/<title[^>]*>([\s\S]*?)<\/title>/i);
  return match ? stripHtmlTags(match[1]) : '';
}

function htmlToPlainText(html: string): string {
  const title = extractHtmlTitle(html);
  const body = stripHtmlTags(html);
  // Prefer body when it has real content; title-only drops the alert narrative.
  if (body && body.length >= 40) {
    if (title && !body.toLowerCase().includes(title.toLowerCase())) {
      return `${title}\n\n${body}`;
    }
    return body;
  }
  return title || body;
}

function normalizeBase64Payload(raw: string): string {
  let compact = raw.replace(/[\s\r\n]/g, '');
  compact = compact.replace(/…+$/u, '').replace(/\.{3}$/, '');
  compact = compact.replace(/[^A-Za-z0-9+/=]+$/, '');
  if (compact.length < 40) return '';
  while (compact.length % 4 !== 0) {
    compact += '=';
  }
  return compact;
}

function decodeBase64Utf8(compact: string): string {
  const binary = atob(compact);
  const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
  return new TextDecoder('utf-8').decode(bytes);
}

function decodeBase64EmailBody(raw: string): string {
  const compact = normalizeBase64Payload(raw);
  if (!compact || !/^[A-Za-z0-9+/=]+$/.test(compact)) return '';
  try {
    const decoded = decodeBase64Utf8(compact).trim();
    if (!decoded) return '';
    const lower = decoded.toLowerCase();
    if (lower.includes('<html') || lower.includes('<!doctype')) {
      return htmlToPlainText(decoded);
    }
    const printable = [...decoded].filter((ch) => {
      const code = ch.charCodeAt(0);
      return code >= 32 || code === 10 || code === 13 || code === 9;
    }).length;
    if (printable / decoded.length < 0.85) return '';
    return decoded;
  } catch {
    return '';
  }
}

function looksLikeEncodedEmailBody(text: string): boolean {
  const compact = text.replace(/[\s\r\n]/g, '').replace(/…+$/u, '');
  return compact.startsWith('PCFET') || compact.startsWith('PGh0bWw') || compact.length >= 120;
}

export function displayIncidentSummary(summary: string | null | undefined): string {
  let text = decodeMimeText(summary);
  if (!text) return '';

  if (looksLikeEncodedEmailBody(text)) {
    const fromBase64 = decodeBase64EmailBody(text);
    if (fromBase64) return fromBase64.trim();
    // Never render raw base64 / HTML-encoded blobs in the investigation canvas.
    if (/^PCFET|^PGh0bWw/i.test(text.replace(/[\s\r\n]/g, ''))) {
      return 'Email body is encoded HTML (could not decode for display). Open raw payload in AMS if needed.';
    }
  }

  const lower = text.toLowerCase();
  if (lower.includes('<html') || lower.includes('<!doctype')) {
    text = htmlToPlainText(text);
  } else {
    const fromBase64 = decodeBase64EmailBody(text);
    if (fromBase64) {
      text = fromBase64;
    }
  }
  const out = text.trim();
  if (/^PCFET|^PGh0bWw/i.test(out.replace(/[\s\r\n]/g, ''))) {
    return 'Email body is encoded HTML (could not decode for display).';
  }
  return out;
}

export function displayTransactionId(tx: string | null | undefined): string {
  if (!tx) return '';
  return decodeMimeText(tx).replace(/^3D/i, '');
}
